/**
 * Libreria — Elenco alunni con settore/classe override per anno scolastico.
 *
 * Caso d'uso: pianificare consegna libri per A.S. successivo (es. 2026/27)
 * con la presunzione di "promozione" senza modificare i dati anagrafici
 * sincronizzati da Segremat Web.
 *
 * Per ogni alunno si visualizza:
 *  - Anagrafica + Ciclo (dal Curriculum)
 *  - Settore (Libreria)  — modificabile, default da ciclo (A/E/M)
 *  - Classe  (Libreria)  — modificabile, default vuoto
 *  - Settore (Segremat) — informativo, da `alunni.SetNom` dell'anno corrente
 *  - Classe  (Segremat) — informativo, da `alunni.ClaNom` dell'anno corrente
 *
 * Excel export: lista filtrata, in 2 formati (tabella unica / raggruppata).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import AnnoAttivoSelector from "@/components/AnnoAttivoSelector";

const SETTORI_LIBRERIA = [
  { value: "BS1A00700E", label: "BS1A00700E — Infanzia" },
  { value: "BS1E00100D", label: "BS1E00100D — Primaria" },
  { value: "BS1E002009", label: "BS1E002009 — Primaria" },
  { value: "BS1M01400R", label: "BS1M01400R — Secondaria" },
];

const CLASSI_BY_SETTORE = {
  BS1A00700E: [], // Infanzia: nessuna classe (non usano libri)
  BS1E00100D: ["1", "2", "3", "4", "5"],
  BS1E002009: ["1", "2", "3", "4", "5"],
  BS1M01400R: ["1A", "1B", "2A", "2B", "3A", "3B"],
};

const CICLO_OPTS = [
  { value: "", label: "Tutti i cicli" },
  { value: "infanzia", label: "Infanzia" },
  { value: "primaria_1", label: "Primaria 1" },
  { value: "primaria_2", label: "Primaria 2" },
  { value: "primaria_3", label: "Primaria 3" },
  { value: "primaria_4", label: "Primaria 4" },
  { value: "primaria_5", label: "Primaria 5" },
  { value: "secondaria_1", label: "Secondaria 1" },
  { value: "secondaria_2", label: "Secondaria 2" },
  { value: "secondaria_3", label: "Secondaria 3" },
  { value: "uscito", label: "Uscito" },
];

export default function LibreriaPage() {
  const { annoCorrente } = useAuth();
  const [anno, setAnno] = useState(annoCorrente || new Date().getFullYear());
  const [data, setData] = useState({ items: [], total: 0, settori_disponibili: [], classi_disponibili: [] });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  // Filtri
  const [q, setQ] = useState("");
  const [filterSet, setFilterSet] = useState("");
  const [filterCla, setFilterCla] = useState("");
  const [filterCiclo, setFilterCiclo] = useState("");
  const [filterOvr, setFilterOvr] = useState(""); // "", "true", "false"
  // Vista
  const [viewMode, setViewMode] = useState("flat"); // "flat" | "grouped"
  // Edit inline
  const [editing, setEditing] = useState({}); // { cod: { settore, classe } }

  useEffect(() => { setAnno(annoCorrente || new Date().getFullYear()); }, [annoCorrente]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { anno };
      const r = await api.get("/libreria/lista", { params });
      setData(r.data);
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore caricamento" });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (anno) load(); /* eslint-disable-next-line */ }, [anno]);

  // Filtri client-side
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return data.items.filter(it => {
      if (ql) {
        const hay = `${it.Cog} ${it.Nom} ${it.cod}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      if (filterSet) {
        const v = (it.settore || "").toUpperCase();
        if (filterSet === "_VUOTO_" ? v !== "" : v !== filterSet.toUpperCase()) return false;
      }
      if (filterCla) {
        const v = (it.classe || "").toUpperCase();
        if (filterCla === "_VUOTO_" ? v !== "" : v !== filterCla.toUpperCase()) return false;
      }
      if (filterCiclo && (it.ciclo || "") !== filterCiclo) return false;
      if (filterOvr === "true" && !it.override_saved) return false;
      if (filterOvr === "false" && it.override_saved) return false;
      return true;
    });
  }, [data.items, q, filterSet, filterCla, filterCiclo, filterOvr]);

  const saveRow = async (cod) => {
    const e = editing[cod];
    if (!e) return;
    try {
      await api.put(`/libreria/${cod}`, { settore: e.settore || "", classe: e.classe || "" }, { params: { anno } });
      setEditing(s => { const c = { ...s }; delete c[cod]; return c; });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Errore salvataggio");
    }
  };

  const removeOverride = async (cod) => {
    if (!window.confirm("Rimuovere l'override libreria per questo alunno? Tornerà ai valori derivati dal Curriculum.")) return;
    try {
      await api.delete(`/libreria/${cod}`, { params: { anno } });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const exportExcel = (grouped = false) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams({ anno: String(anno) });
    if (q) params.set("q", q);
    if (filterSet) params.set("settore", filterSet);
    if (filterCla) params.set("classe", filterCla);
    if (filterCiclo) params.set("ciclo", filterCiclo);
    if (filterOvr) params.set("solo_override", filterOvr);
    if (grouped) params.set("group", "true");
    window.open(`${base}/api/libreria/export-excel?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  const upgradeSimulato = async () => {
    const overwrite = window.confirm(
      `Upgrade simulato per A.S. ${anno}/${String(anno + 1).slice(-2)}\n\n` +
      `Regole applicate:\n` +
      `• Primaria 2-5 + Segremat E → BS1E00100D + classe N\n` +
      `• Primaria 2-5 + Segremat F → BS1E002009 + classe N\n` +
      `• Secondaria 2-3 + 1A/1B/2A/2B → BS1M01400R + (N)A/B\n` +
      `• Primaria 1 e Secondaria 1: NON toccati (compili a mano)\n\n` +
      `OK → SOVRASCRIVI anche override già salvati\n` +
      `Annulla → solo per alunni SENZA override (più sicuro)\n\n` +
      `Premi OK per sovrascrivere, Annulla per "solo nuovi".`
    );
    // Conferma finale
    if (!window.confirm(`Procedere con l'upgrade simulato? (${overwrite ? "SOVRASCRIVI" : "solo nuovi"})`)) return;
    try {
      const r = await api.post("/libreria/upgrade-simulato", { anno, overwrite });
      const d = r.data;
      setMsg({
        ok: true,
        text: `Upgrade completato: ${d.applied} alunni aggiornati · ${d.skipped_existing} saltati (override già presenti) · ${d.no_rule} senza regola applicabile (su ${d.total_evaluated} totali)`,
      });
      await load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore upgrade simulato" });
    }
  };

  // Raggruppamento per vista
  const grouped = useMemo(() => {
    if (viewMode !== "grouped") return null;
    const map = {};
    filtered.forEach(it => {
      const key = `${(it.settore || "_").toUpperCase()}|${(it.classe || "_").toUpperCase()}`;
      if (!map[key]) map[key] = { settore: it.settore || "", classe: it.classe || "", items: [] };
      map[key].items.push(it);
    });
    return Object.values(map).sort((a, b) => {
      const sa = (a.settore || "ZZZ").toUpperCase();
      const sb = (b.settore || "ZZZ").toUpperCase();
      if (sa !== sb) return sa.localeCompare(sb);
      return (a.classe || "ZZZ").localeCompare(b.classe || "ZZZ");
    });
  }, [viewMode, filtered]);

  const totals = useMemo(() => {
    const overr = filtered.filter(it => it.override_saved).length;
    return { tot: filtered.length, overr };
  }, [filtered]);

  return (
    <div data-testid="libreria-page" style={{ padding: 16, maxWidth: "100%" }}>
      <h2 style={{ margin: "0 0 12px", color: "#0d47a1", fontSize: 20 }}>📚 Elenco Alunni Libreria</h2>

      {msg && (
        <div style={{ padding: "8px 12px", marginBottom: 10, borderRadius: 3, background: msg.ok ? "#e8f5e9" : "#ffebee", color: msg.ok ? "#1b5e20" : "#c62828", fontSize: 12 }}>
          {msg.text}
        </div>
      )}

      {/* Toolbar filtri */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "10px 12px", background: "#fafafa", border: "1px solid #e0e0e0", borderRadius: 4, marginBottom: 10 }}>
        <AnnoAttivoSelector />

        <label style={{ fontSize: 12, fontWeight: 700, color: "#0d47a1", marginLeft: 4 }}>Ciclo:</label>
        <select data-testid="lib-filter-ciclo" value={filterCiclo} onChange={e => setFilterCiclo(e.target.value)}
          style={{ padding: "5px 8px", fontSize: 12 }}>
          {CICLO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <input data-testid="lib-search" type="text" placeholder="🔍 Cognome / Nome / Cod"
          value={q} onChange={e => setQ(e.target.value)}
          style={{ padding: "5px 8px", fontSize: 12, minWidth: 220 }} />

        <select data-testid="lib-filter-settore" value={filterSet} onChange={e => setFilterSet(e.target.value)}
          style={{ padding: "5px 8px", fontSize: 12 }}>
          <option value="">Settore: tutti</option>
          {SETTORI_LIBRERIA.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          <option value="_VUOTO_">(senza settore)</option>
        </select>

        <select data-testid="lib-filter-classe" value={filterCla} onChange={e => setFilterCla(e.target.value)}
          style={{ padding: "5px 8px", fontSize: 12 }}>
          <option value="">Classe: tutte</option>
          {data.classi_disponibili.map(c => <option key={c} value={c}>{c}</option>)}
          <option value="_VUOTO_">(senza classe)</option>
        </select>

        <select data-testid="lib-filter-ovr" value={filterOvr} onChange={e => setFilterOvr(e.target.value)}
          style={{ padding: "5px 8px", fontSize: 12 }}>
          <option value="">Override: tutti</option>
          <option value="true">Solo override salvati</option>
          <option value="false">Solo default (non salvati)</option>
        </select>

        {(q || filterSet || filterCla || filterCiclo || filterOvr) && (
          <button onClick={() => { setQ(""); setFilterSet(""); setFilterCla(""); setFilterCiclo(""); setFilterOvr(""); }}
            style={{ padding: "5px 10px", background: "#757575", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            Pulisci
          </button>
        )}

        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "#555", marginRight: 6 }}>
            <b>{totals.tot}</b> alunni · <b>{totals.overr}</b> con override
          </div>
          <div style={{ display: "flex", border: "1px solid #ccc", borderRadius: 3 }}>
            <button data-testid="lib-view-flat" onClick={() => setViewMode("flat")}
              style={{ padding: "5px 10px", background: viewMode === "flat" ? "#0d47a1" : "white", color: viewMode === "flat" ? "white" : "#333", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              📄 Tabella
            </button>
            <button data-testid="lib-view-grouped" onClick={() => setViewMode("grouped")}
              style={{ padding: "5px 10px", background: viewMode === "grouped" ? "#0d47a1" : "white", color: viewMode === "grouped" ? "white" : "#333", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              📑 Per Classe
            </button>
          </div>
          <button data-testid="lib-upgrade" onClick={upgradeSimulato}
            title="Compila automaticamente Settore/Classe Libreria per i cicli intermedi (P2-P5, M2-M3) usando il Segremat dell'anno precedente"
            style={{ padding: "6px 12px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            🔄 Upgrade Simulato
          </button>
          <button data-testid="lib-export" onClick={() => exportExcel(viewMode === "grouped")}
            style={{ padding: "6px 12px", background: "#1b5e20", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            📥 Esporta Excel
          </button>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Nessun alunno corrisponde ai filtri.</div>
      ) : viewMode === "flat" ? (
        <FlatTable rows={filtered} editing={editing} setEditing={setEditing} saveRow={saveRow} removeOverride={removeOverride} settoriDisp={data.settori_disponibili} anno={anno} />
      ) : (
        <GroupedView groups={grouped || []} editing={editing} setEditing={setEditing} saveRow={saveRow} removeOverride={removeOverride} settoriDisp={data.settori_disponibili} />
      )}
    </div>
  );
}

function FlatTable({ rows, editing, setEditing, saveRow, removeOverride, settoriDisp, anno }) {
  return (
    <div style={{ overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 4 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#0d47a1", color: "white", position: "sticky", top: 0, zIndex: 1 }}>
            <th style={th}>Cod</th>
            <th style={th}>Cognome</th>
            <th style={th}>Nome</th>
            <th style={th}>Data Nascita</th>
            <th style={th}>Ciclo</th>
            <th style={{ ...th, background: "#1565c0" }}>Settore (Libreria)</th>
            <th style={{ ...th, background: "#1565c0" }}>Classe (Libreria)</th>
            <th style={th} title={`Anno scolastico ${anno - 1}/${String(anno).slice(-2)}`}>Settore (Segremat {String(anno - 1).slice(-2)}/{String(anno).slice(-2)})</th>
            <th style={th} title={`Anno scolastico ${anno - 1}/${String(anno).slice(-2)}`}>Classe (Segremat {String(anno - 1).slice(-2)}/{String(anno).slice(-2)})</th>
            <th style={th}>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(it => (
            <Row key={it.cod} it={it} editing={editing} setEditing={setEditing} saveRow={saveRow} removeOverride={removeOverride} settoriDisp={settoriDisp} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupedView({ groups, editing, setEditing, saveRow, removeOverride, settoriDisp }) {
  return (
    <div>
      {groups.map(g => {
        const headerLabel = `${g.settore || "(senza settore)"} — ${g.classe || "(senza classe)"}`;
        return (
          <div key={`${g.settore}|${g.classe}`} style={{ marginBottom: 14, border: "1px solid #ccc", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ padding: "8px 12px", background: "#0d47a1", color: "white", fontWeight: 700, fontSize: 13 }}>
              {headerLabel} <span style={{ marginLeft: 8, opacity: 0.85, fontSize: 11, fontWeight: 400 }}>· {g.items.length} alunni</span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#e3f2fd", color: "#0d47a1" }}>
                  <th style={th}>Cod</th>
                  <th style={th}>Cognome</th>
                  <th style={th}>Nome</th>
                  <th style={th}>Data Nascita</th>
                  <th style={th}>Ciclo</th>
                  <th style={th}>Sett. (Lib.)</th>
                  <th style={th}>Cl. (Lib.)</th>
                  <th style={th}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map(it => (
                  <Row key={it.cod} it={it} editing={editing} setEditing={setEditing} saveRow={saveRow} removeOverride={removeOverride} settoriDisp={settoriDisp} compact />
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function Row({ it, editing, setEditing, saveRow, removeOverride, settoriDisp, compact }) {
  const e = editing[it.cod];
  const isEditing = !!e;
  const settoreVal = isEditing ? e.settore : it.settore;
  const classeVal = isEditing ? e.classe : it.classe;
  const beginEdit = () => setEditing(s => ({ ...s, [it.cod]: { settore: it.settore || "", classe: it.classe || "" } }));
  const cancelEdit = () => setEditing(s => { const c = { ...s }; delete c[it.cod]; return c; });
  const update = (k, v) => setEditing(s => {
    const next = { ...s[it.cod], [k]: v };
    // Cambio settore → reset classe se incompatibile
    if (k === "settore") {
      const allowed = CLASSI_BY_SETTORE[v] || [];
      if (!allowed.includes(next.classe)) next.classe = "";
    }
    return { ...s, [it.cod]: next };
  });

  const allowedClassi = CLASSI_BY_SETTORE[settoreVal] || [];

  return (
    <tr style={{ borderBottom: "1px solid #eee", background: it.override_saved ? "#fffbe7" : "white" }}
        data-testid={`lib-row-${it.cod}`}>
      <td style={{ ...td, fontFamily: "Consolas, monospace" }}>{it.cod}</td>
      <td style={td}>{it.Cog}</td>
      <td style={td}>{it.Nom}</td>
      <td style={{ ...td, fontSize: 11, color: "#555" }}>{it.NasDat}</td>
      <td style={{ ...td, fontSize: 11, fontWeight: 700, color: "#0d47a1" }}>{it.ciclo_label || "—"}</td>
      <td style={{ ...td, background: isEditing ? "#fff9c4" : undefined }}>
        {isEditing ? (
          <select data-testid={`lib-set-${it.cod}`} value={settoreVal} onChange={ev => update("settore", ev.target.value)}
            style={{ padding: "3px 5px", fontSize: 11, width: "100%" }}>
            <option value="">(vuoto)</option>
            {SETTORI_LIBRERIA.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        ) : (
          <span data-testid={`lib-set-${it.cod}-val`} style={{ fontWeight: 700, color: it.override_saved ? "#e65100" : "#1565c0", fontFamily: "Consolas, monospace", fontSize: 11 }}>
            {it.settore || "—"}
          </span>
        )}
      </td>
      <td style={{ ...td, background: isEditing ? "#fff9c4" : undefined }}>
        {isEditing ? (
          allowedClassi.length === 0 ? (
            <span style={{ fontSize: 10, color: "#888", fontStyle: "italic" }}>
              {settoreVal === "BS1A00700E" ? "(no libri)" : "(scegli settore)"}
            </span>
          ) : (
            <select data-testid={`lib-cla-${it.cod}`} value={classeVal}
              onChange={ev => update("classe", ev.target.value)}
              style={{ padding: "3px 5px", fontSize: 12, width: "100%" }}>
              <option value="">—</option>
              {allowedClassi.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )
        ) : (
          <span data-testid={`lib-cla-${it.cod}-val`} style={{ fontWeight: 700, color: it.override_saved ? "#e65100" : "#888" }}>
            {it.classe || "—"}
          </span>
        )}
      </td>
      {!compact && (
        <>
          <td style={{ ...td, fontSize: 11, color: "#000", fontFamily: "Consolas, monospace", fontWeight: 700 }}>{it.settore_originale || "—"}</td>
          <td style={{ ...td, fontSize: 11, color: "#000", fontWeight: 700 }}>{it.classe_originale || "—"}</td>
        </>
      )}
      <td style={{ ...td, whiteSpace: "nowrap" }}>
        {isEditing ? (
          <>
            <button data-testid={`lib-save-${it.cod}`} onClick={() => saveRow(it.cod)}
              style={{ padding: "3px 8px", background: "#1b5e20", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 3 }}>
              ✓ Salva
            </button>
            <button onClick={cancelEdit}
              style={{ padding: "3px 8px", background: "#757575", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
              ✕
            </button>
          </>
        ) : (
          <>
            <button data-testid={`lib-edit-${it.cod}`} onClick={beginEdit}
              style={{ padding: "3px 8px", background: "#1565c0", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, marginRight: 3 }}>
              ✏️ Modifica
            </button>
            {it.override_saved && (
              <button data-testid={`lib-clear-${it.cod}`} onClick={() => removeOverride(it.cod)}
                title="Rimuovi override (torna ai valori derivati)"
                style={{ padding: "3px 8px", background: "#c62828", color: "white", border: "none", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2 }}>
                ↺
              </button>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

const th = { padding: "8px 8px", fontSize: 11, textAlign: "left", borderBottom: "1px solid #ddd" };
const td = { padding: "5px 8px", verticalAlign: "middle", textAlign: "left" };
